import Vue from 'vue';
import Requests from '@/helpers/Request';
import { toJson } from '@/helpers/EpgGuide';
import { buildWindow, normalizePrograms, indexOfCurrentProgram } from '@/helpers/EpgGuide2';

// Jetzt-/Danach-Daten pro Sender fuer das OSD der TV-Kanalliste.
//
// Warum ein eigenes Modul:
// Bisher hat HOME beim ersten Aufruf den kompletten TV-Guide v1 vorgeladen
// (fetchTvPackages + ein XML mit 110 Events je Sender + drei synchrone
// Parse-Bursts je Sender), nur damit das OSD im TVPlayer zwei Textzeilen
// anzeigen kann. Gebraucht werden dort aber die laufende und die naechste
// Sendung des EINEN fokussierten Senders. Genau das holt dieses Modul - bei
// Bedarf, klein und pro Sender gecached.
//
// Ausserdem war der Zugriff auf die Guide-Daten positionsabhaengig
// (Object.entries(state.epgGuide)[3]/[4] in TVPlayer.getEPGData): eine neue
// Zeile im state()-Objekt von epg-guide.js haette das OSD stillschweigend
// zerstoert. Hier gibt es dafuer Getter.
//
// Die Senderlogos kommen NICHT von hier und kamen auch nie aus dem Guide: die
// liefert tv/prepare ueber fetchAllEPGData (tv.js, state.epgData /
// getters.getEpgById), angestossen aus navigation/init. Sie sind vom Guide
// also schon immer unabhaengig.

// Events je Sender. Zwei werden angezeigt, der Rest ist Reserve, damit ein
// Sendungswechsel im OSD keinen neuen Request braucht.
const EVENT_COUNT = 8;

// Zeitfenster fuer normalizePrograms. Grosszuegig genug fuer EVENT_COUNT lange
// Sendungen (Spielfilm-Nacht), klein genug, um Muell fernzuhalten.
const WINDOW_HOURS = 12;

// Danach gelten die Daten eines Senders als alt.
const MAX_AGE_MS = 30 * 60 * 1000;

// Reichen die gecachten Sendungen nicht mehr so weit in die Zukunft, wird neu
// geholt - auch wenn MAX_AGE_MS noch nicht erreicht ist. Sonst steht das OSD
// irgendwann ohne "danach" da, obwohl der Cache formal frisch ist.
const FUTURE_MARGIN_MS = 15 * 60 * 1000;

// Vorwaermen beim App-Start: so viele Sender, so viele gleichzeitig.
// Klein halten - das OSD zeigt immer nur den EINEN fokussierten Sender
// (TVPlayer.getChannelsEPG), und den holt requestEpgForTarget(0) beim Einstieg
// ohnehin sofort. Der Vorrat hier deckt nur die ersten Schritte beim
// Durchblaettern ab, alles Weitere holt ensureChannel nach.
const PREWARM_CHANNELS = 3;
const PREWARM_BATCH = 2;

// Laufende Requests bewusst ausserhalb des States: Promises gehoeren nicht in
// reaktiven State und wuerden von Vue nur unnoetig observiert.
const inFlight = {};

function yieldToEventLoop() {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

function isFresh(entry, nowMs) {
  if (!entry || entry.status !== 'ok') return false;
  if (nowMs - entry.loadedAt >= MAX_AGE_MS) return false;
  const programs = entry.programs || [];
  if (!programs.length) return false;
  return programs[programs.length - 1].endMs > nowMs + FUTURE_MARGIN_MS;
}

export default {
  namespaced: true,
  state: () => ({
    // { [epgChannelId]: { programs: [...], loadedAt, status } }
    // status: 'ok' | 'empty' | 'error'
    byChannel: {},
  }),
  mutations: {
    setChannel(state, { epgChannelId, programs, status }) {
      Vue.set(state.byChannel, String(epgChannelId), {
        programs: programs || [],
        loadedAt: Date.now(),
        status: status || 'ok',
      });
    },
    clear(state) {
      state.byChannel = {};
    },
  },
  actions: {
    // Sorgt dafuer, dass fuer einen Sender Jetzt-/Danach-Daten im Cache liegen.
    // Mehrfachaufrufe fuer denselben Sender teilen sich einen Request.
    ensureChannel({ state, commit, rootState }, options) {
      const opts = options || {};
      const id = opts.epgChannelId;
      if (!(Number(id) > 0)) return Promise.resolve([]);

      const key = String(id);
      const nowMs = Date.now();

      if (!opts.force && isFresh(state.byChannel[key], nowMs)) {
        return Promise.resolve(state.byChannel[key].programs);
      }
      if (inFlight[key]) return inFlight[key];

      // Hat der TV-Guide v2 den Sender schon geladen, ist das dieselbe Quelle
      // im selben Format - dann kostet uns das keinen Request.
      const guide = rootState.epgGuide2 && rootState.epgGuide2.programsByChannel;
      const fromGuide = guide && guide[key];
      if (
        !opts.force &&
        fromGuide &&
        fromGuide.length &&
        fromGuide[fromGuide.length - 1].endMs > nowMs + FUTURE_MARGIN_MS
      ) {
        commit('setChannel', { epgChannelId: key, programs: fromGuide, status: 'ok' });
        return Promise.resolve(fromGuide);
      }

      const window = buildWindow(new Date(nowMs), WINDOW_HOURS);
      const request = Requests.fetchEpgForChannel({
        epgChannelId: id,
        number: EVENT_COUNT,
      })
        .then((xml) => {
          let programs = [];
          try {
            programs = normalizePrograms(toJson(xml), window);
          } catch (e) {
            console.log('[epgNow] Parsen fehlgeschlagen fuer ' + id + ':', e);
          }
          commit('setChannel', {
            epgChannelId: key,
            programs: programs,
            status: programs.length ? 'ok' : 'empty',
          });
          return programs;
        })
        .catch((err) => {
          console.log('[epgNow] EPG-Fetch fehlgeschlagen fuer ' + id + ':', err);
          commit('setChannel', { epgChannelId: key, programs: [], status: 'error' });
          return [];
        });

      // Kein Promise.prototype.finally - das fehlt auf den alten
      // Chrome-Versionen der LG-Geraete.
      const release = function (result) {
        delete inFlight[key];
        return result;
      };
      inFlight[key] = request.then(release, release);
      return inFlight[key];
    },

    // Beim App-Start die ersten Sender vorwaermen, damit die Kanalliste beim
    // ersten Oeffnen sofort Programmdaten zeigt. Gedrosselt und sequenziell,
    // damit es den Start nicht ausbremst.
    async prewarm({ dispatch, rootGetters }, options) {
      const opts = options || {};
      const limit = opts.limit || PREWARM_CHANNELS;
      const channels = (rootGetters['tv/getChannels'] || [])
        .filter((c) => c && c.serviceType === 'tv' && Number(c.epgChannelId) > 0)
        .slice(0, limit);

      for (let i = 0; i < channels.length; i += PREWARM_BATCH) {
        const batch = channels.slice(i, i + PREWARM_BATCH);
        await Promise.all(
          batch.map((c) => dispatch('ensureChannel', { epgChannelId: c.epgChannelId }))
        );
        await yieldToEventLoop();
      }
    },
  },
  getters: {
    // Laufende und naechste Sendung. Leeres Array, solange nichts geladen ist -
    // die Seite muss nichts pruefen.
    //
    // nowMs wird uebergeben statt hier Date.now() zu lesen: ein Getter mit
    // Date.now() waere nicht reaktiv und das OSD wuerde beim Sendungswechsel
    // nicht nachziehen.
    getNowNext: (state) => (epgChannelId, nowMs) => {
      const entry = state.byChannel[String(epgChannelId)];
      const programs = (entry && entry.programs) || [];
      if (!programs.length) return [];
      const at = nowMs || Date.now();
      const index = indexOfCurrentProgram(programs, at);
      const out = [];
      if (programs[index]) out.push(programs[index]);
      if (programs[index + 1]) out.push(programs[index + 1]);
      return out;
    },
    getStatus: (state) => (epgChannelId) => {
      const entry = state.byChannel[String(epgChannelId)];
      return (entry && entry.status) || 'pending';
    },
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/epg-now.js